/**
 * Configuración central del brand — lado frontend (Vite).
 *
 * Todos los valores se leen desde variables de entorno VITE_BRAND_*.
 * Configure las variables en Vercel para personalizar el brand.
 *
 * Para adaptar este proyecto a otro brand (multi-creator SaaS):
 *   1. Cambiar las VITE_BRAND_* en Vercel (producción) o en .env.local (local).
 *   2. NO es necesario tocar ningún componente React.
 *
 * Variables gemelas en el backend:
 *   - Frontend: src/lib/brandConfig.ts  (VITE_BRAND_* con prefijo VITE_)
 *   - Backend:  api/lib/brandConfig.ts  (BRAND_* sin prefijo)
 * Ambos deben compartir la misma convención de nombres.
 */

import { edgeRuntimeString } from "@/lib/edgeRuntimeConfig";
import { readRuntimeBrandEnv } from "@/lib/runtimeBrandEnv";

// ── Identidad ────────────────────────────────────────────────────────────────

/**
 * Nombre legal / real de la persona creadora.
 * Usado en StructuredData JSON-LD, i18n, SEO, alt text.
 */
export const BRAND_PERSON_NAME =
  import.meta.env.VITE_BRAND_PERSON_NAME ?? "";

/**
 * Nombre público del brand / handle.
 * Puede ser diferente al nombre legal. Usado en footers, títulos, emails.
 */
export const BRAND_NAME =
  import.meta.env.VITE_BRAND_NAME ?? "";

/**
 * Clave corta en minúsculas, sin espacios (ej. "delfuocampo").
 * Usada en:
 *   - Stripe/MP metadata `source` → identifica pagos del brand
 *   - `client_reference_id` en URLs de pago
 *   - Mux `creator_id` para filtrar videos
 *   - PostHog `site` property
 *   - DB: manual_transactions.source, site, brand
 * DEBE coincidir con BRAND_SOURCE_KEY en el backend.
 */
export const BRAND_SOURCE_KEY =
  (import.meta.env.VITE_BRAND_SOURCE_KEY as string)?.trim() ||
  edgeRuntimeString("brandSourceKey") ||
  "";

/**
 * Handle en redes sociales (sin @).
 * Usado en StructuredData alternateName, SEO keywords.
 */
export const BRAND_HANDLE =
  import.meta.env.VITE_BRAND_HANDLE ?? BRAND_SOURCE_KEY;

/**
 * Edad del creador para mostrar en bio y SEO.
 * Usada en descripciones, metadatos y textos del sitio.
 *
 * GOTCHA: las VITE_* son BUILD-TIME (se hornean en `vite build`). Cambiar el
 * valor en Railway NO basta: el cache de Docker (`RUN npm run build`) no se
 * invalida por un cambio de variable solo → hace falta un rebuild con cache-miss
 * (un cambio de source, como este). Sin el valor, queda "" → SEO muestra " años".
 */
export const BRAND_AGE =
  import.meta.env.VITE_BRAND_AGE ?? "";

// ── URLs ─────────────────────────────────────────────────────────────────────

/** URL del sitio principal (este proyecto). Ej. "https://www.delfuocampo.com" */
export const BRAND_SITE_URL =
  import.meta.env.VITE_BRAND_SITE_URL ??
  import.meta.env.VITE_MAIN_SITE_URL ??
  (BRAND_SOURCE_KEY ? `https://www.${BRAND_SOURCE_KEY}.com` : "");

/**
 * Hostname corto del sitio principal, sin protocolo ni www.
 * Ej. "delfuocampo.com". Usado en footer links, siteGeoPolicy.
 */
export const BRAND_SITE_HOST = BRAND_SITE_URL
  ? BRAND_SITE_URL.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")
  : "";

/** Hostname for display — never throws even when VITE_BRAND_SITE_URL is missing in build. */
export function getBrandSiteHostname(): string {
  if (BRAND_SITE_HOST) return BRAND_SITE_HOST;
  try {
    if (BRAND_SITE_URL) return new URL(BRAND_SITE_URL).hostname;
  } catch {
    /* invalid URL in env */
  }
  return BRAND_SOURCE_KEY ? `${BRAND_SOURCE_KEY}.com` : "delfuocampo.com";
}

/** Primer string URL no vacío (tras trim); ignora "" para que `??` en cadena anterior no bloquee fallbacks. */
function firstNonEmptyUrl(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    const t = String(c ?? "").trim();
    if (t) return t;
  }
  return undefined;
}

// ── Pagos ────────────────────────────────────────────────────────────────────

/**
 * `client_reference_id` que se añade a URLs de Stripe/MercadoPago.
 * Igual que BRAND_SOURCE_KEY — centralizado aquí para claridad.
 */
export const BRAND_CLIENT_REFERENCE_ID = BRAND_SOURCE_KEY;

// ── Mux ──────────────────────────────────────────────────────────────────────

/**
 * `creator_id` usado como filtro en la API de Mux Data.
 * Identifica los assets de este brand en una cuenta Mux compartida.
 */
export const BRAND_MUX_CREATOR_ID =
  import.meta.env.VITE_BRAND_MUX_CREATOR_ID ?? BRAND_SOURCE_KEY;

// ── i18n / SEO ───────────────────────────────────────────────────────────────

/** Nombre del sitio para i18n, Open Graph, StructuredData. */
export const BRAND_SITE_NAME =
  import.meta.env.VITE_BRAND_SITE_NAME ?? BRAND_PERSON_NAME;

/**
 * Nombre para JSON-LD / SEO — nunca vacío (GSC exige Person.name).
 *
 * `BRAND_PERSON_NAME` es una constante de build: si el deploy corrió sin
 * `VITE_BRAND_PERSON_NAME` queda "" y las plantillas emiten frases colgadas
 * ("tu membresía en ."). El Worker sí publica el nombre en
 * `/api/public/brand-runtime-config`, así que resolvemos por la misma cadena
 * que el resto de las VITE_*: build → edge HTML → brand-runtime.
 */
export function getBrandDisplayName(): string {
  const person = readRuntimeBrandEnv("VITE_BRAND_PERSON_NAME");
  if (person) return person;
  const brand = BRAND_NAME?.trim();
  if (brand) return brand;
  const key = BRAND_SOURCE_KEY?.trim();
  if (key) return key;
  return "Delfu Ocampo";
}

// ── Emails (frontend-visible) ─────────────────────────────────────────────────
/** Email de soporte visible al usuario (ej. "hola@delfuocampo.com"). */
export const BRAND_SUPPORT_EMAIL =
  import.meta.env.VITE_BRAND_SUPPORT_EMAIL ??
  import.meta.env.VITE_SUPPORT_EMAIL ??
  (BRAND_SITE_HOST ? `hola@${BRAND_SITE_HOST}` : "");

// ── URLs de Subdomains ───────────────────────────────────────────────────────

/** URL del admin dashboard/menu (ej. "https://menu.delfuocampo.com"). */
export const BRAND_MENU_URL =
  import.meta.env.VITE_BRAND_MENU_URL ??
  (BRAND_SOURCE_KEY ? `https://menu.${BRAND_SOURCE_KEY}.com` : "");

// ── Social Media ─────────────────────────────────────────────────────────────

/** Handle de Instagram sin @ (ej. "delfuocampo"). */
export const BRAND_INSTAGRAM_HANDLE =
  import.meta.env.VITE_BRAND_INSTAGRAM_HANDLE ?? BRAND_HANDLE;

/** Handle de TikTok sin @ (ej. "micreadora"). */
export const BRAND_TIKTOK_HANDLE =
  import.meta.env.VITE_BRAND_TIKTOK_HANDLE ?? BRAND_HANDLE;

/** Handle de Telegram sin @ (ej. "micreadora"). */
export const BRAND_TELEGRAM_HANDLE =
  import.meta.env.VITE_BRAND_TELEGRAM_HANDLE ?? BRAND_HANDLE;

/** Handle de YouTube sin @ (ej. "micreadora"). */
export const BRAND_YOUTUBE_HANDLE =
  import.meta.env.VITE_BRAND_YOUTUBE_HANDLE ?? BRAND_HANDLE;

/** URL completa de Instagram. */
export const BRAND_INSTAGRAM_URL =
  import.meta.env.VITE_BRAND_INSTAGRAM_URL ??
  (BRAND_INSTAGRAM_HANDLE ? `https://www.instagram.com/${BRAND_INSTAGRAM_HANDLE}` : "");

/** URL completa de TikTok. */
export const BRAND_TIKTOK_URL =
  import.meta.env.VITE_BRAND_TIKTOK_URL ??
  (BRAND_TIKTOK_HANDLE ? `https://www.tiktok.com/@${BRAND_TIKTOK_HANDLE}` : "");

/** URL completa de Telegram. */
export const BRAND_TELEGRAM_URL =
  import.meta.env.VITE_BRAND_TELEGRAM_URL ??
  (BRAND_TELEGRAM_HANDLE ? `https://t.me/${BRAND_TELEGRAM_HANDLE}` : "");

/** URL completa de YouTube. */
export const BRAND_YOUTUBE_URL =
  import.meta.env.VITE_BRAND_YOUTUBE_URL ??
  (BRAND_YOUTUBE_HANDLE ? `https://www.youtube.com/@${BRAND_YOUTUBE_HANDLE}` : "");

// ── CDN / Assets ─────────────────────────────────────────────────────────────

/**
 * Carpeta raíz en la Media Library de Cloudinary para este creador (ej. "delfuocampo").
 * Usa `||` en vez de `??` para que strings vacíos `""` no bloqueen el fallback.
 * `VITE_CLOUDINARY_CLOUD_NAME` es el id de la cuenta compartida (subdominio API), no una ruta de carpeta.
 */
export const BRAND_CLOUDINARY_FOLDER =
  (import.meta.env.VITE_BRAND_CLOUDINARY_FOLDER as string)?.trim() ||
  (import.meta.env.VITE_CLOUDINARY_BASE_FOLDER as string)?.trim() ||
  edgeRuntimeString("cloudinaryBaseFolder") ||
  BRAND_SOURCE_KEY ||
  "";

/** URL del banner/header image principal. */
export const BRAND_BANNER_IMAGE_URL =
  import.meta.env.VITE_BRAND_BANNER_IMAGE_URL ??
  import.meta.env.VITE_BANNER_IMAGE_URL;

/**
 * Avatar / logo circular del brand (Cloudinary).
 * Usado en `/sorprendeme`, previews VIP y admin Dual CTA.
 * Override: `VITE_BRAND_AVATAR_URL`.
 */
export const BRAND_AVATAR_URL =
  (import.meta.env.VITE_BRAND_AVATAR_URL as string)?.trim() ||
  "https://res.cloudinary.com/nm-creators/image/upload/v1783548580/avatar-delfu_ljfix1.png";

/**
 * Fondo full-bleed de `/vip/:slug`.
 * Override: `VITE_VIP_PAGE_BACKGROUND_MEDIA_URL` o `VITE_BRAND_VIP_PAGE_BACKGROUND_URL`.
 */
export const BRAND_VIP_PAGE_BACKGROUND_URL =
  (import.meta.env.VITE_VIP_PAGE_BACKGROUND_MEDIA_URL as string)?.trim() ||
  (import.meta.env.VITE_BRAND_VIP_PAGE_BACKGROUND_URL as string)?.trim() ||
  "https://res.cloudinary.com/nm-creators/image/upload/v1778447625/header-banner-image-mobile_igowxk.png";

/**
 * Badge "Powered by Stripe" (blanco) — confianza en flujos no-AR
 * (`/vip/:slug` intl, `/sorprendeme` Stripe, StripeCheckoutForm).
 * Override: `VITE_BRAND_POWERED_BY_STRIPE_URL`.
 */
export const BRAND_POWERED_BY_STRIPE_URL =
  (import.meta.env.VITE_BRAND_POWERED_BY_STRIPE_URL as string)?.trim() ||
  "https://res.cloudinary.com/nm-creators/image/upload/v1781711564/Powered_by_Stripe_-_white_csam6q.svg";

/**
 * Logo handshake Mercado Pago (color) — confianza en checkout AR / Mux CTA VIP.
 * Mismo asset que CumpleTemplate y MercadoPagoCtaButton.
 * Override: `VITE_BRAND_MERCADOPAGO_HANDSHAKE_URL`.
 */
export const BRAND_MERCADOPAGO_HANDSHAKE_URL =
  (import.meta.env.VITE_BRAND_MERCADOPAGO_HANDSHAKE_URL as string)?.trim() ||
  "https://res.cloudinary.com/nm-creators/image/upload/v1781484845/MP_RGB_HANDSHAKE_color_horizontal_jeshrv.png";

/**
 * Default preview image for VIP Video Assets (Dual CTA / membership visuals).
 * Same asset as `BRAND_AVATAR_URL` unless a dedicated preview env is set later.
 */
export const BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE = BRAND_AVATAR_URL;

// Alias for clarity in VIP asset context
export const BRAND_VIP_VIDEO_PREVIEW_IMAGE = BRAND_DEFAULT_VIP_VIDEO_PREVIEW_IMAGE;

/**
 * Metadata base de pagos (Stripe / MercadoPago).
 * Siempre incluye `source: BRAND_SOURCE_KEY` — no se puede omitir ni pisar con "".
 * Usar en `/vip/:slug`, `/sorprendeme` y cualquier checkout del brand.
 */
export function brandPaymentSourceMetadata(
  extra: Record<string, string | number | boolean | null | undefined> = {},
): Record<string, string> {
  const out: Record<string, string> = {
    source: BRAND_SOURCE_KEY || "delfuocampo",
  };
  for (const [k, v] of Object.entries(extra)) {
    if (k === "source") continue; // source always from brandConfig
    if (v === null || v === undefined || v === "") continue;
    out[k] = String(v);
  }
  return out;
}

// ── Storage Keys (Frontend) ──────────────────────────────────────────────────

/** Prefijo para localStorage/sessionStorage keys (ej. "delfuocampo"). */
export const BRAND_STORAGE_PREFIX = BRAND_SOURCE_KEY;

// ── SEO & Metadata ───────────────────────────────────────────────────────────

/** Descripción del brand para meta tags y SEO. */
export const BRAND_DESCRIPTION =
  import.meta.env.VITE_BRAND_DESCRIPTION ??
  "";

/** Locale principal (ej. "es_AR"). */
export const BRAND_LOCALE = import.meta.env.VITE_BRAND_LOCALE ?? "es_AR";

/** Código de idioma (ej. "es"). */
export const BRAND_LANGUAGE = import.meta.env.VITE_BRAND_LANGUAGE ?? "es";

/** País del brand (ej. "Argentina"). */
export const BRAND_COUNTRY = import.meta.env.VITE_BRAND_COUNTRY ?? "Argentina";

/** URL de la imagen OG/Twitter. */
export const BRAND_OG_IMAGE_URL =
  import.meta.env.VITE_BRAND_OG_IMAGE_URL ??
  (BRAND_SITE_URL ? `${BRAND_SITE_URL}/og.png` : "");

/** Logo HTTPS para UI embebida (p. ej. Clerk SignUp/SignIn). Cuadrado recomendado. */
export const BRAND_AUTH_LOGO_URL =
  import.meta.env.VITE_BRAND_AUTH_LOGO_URL ??
  import.meta.env.VITE_BRAND_LOGO_URL ??
  BRAND_OG_IMAGE_URL;

/** Código de verificación de Google Search Console. */
export const BRAND_GSC_VERIFICATION =
  import.meta.env.VITE_BRAND_GSC_VERIFICATION ??
  "";
